/**
 * ClosureRequestCard — T-42 (leadership dashboard + project Settings).
 *
 * One pending project closure request: project (link), the requester's reason, requester + time.
 * Leadership/admin Approve (the server closes the project in the same transaction) or Reject
 * (reveals an inline note — required). On resolve the lists refetch and the card disappears.
 *
 * `PendingClosureRequestsSection` renders the cross-project pending queue for the Dashboard. It is
 * self-contained (loading / error / empty states) so the Dashboard only mounts it.
 */
import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Archive, Check, Loader2, X } from "lucide-react";

import type { ClosureRequest } from "@/types";
import {
  usePendingClosureRequests,
  useResolveClosureRequest,
} from "@/hooks/useClosureRequests";
import { apiErrorMessage } from "@/lib/api";
import { timeAgo } from "@/lib/date-change";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

/** Mirrors CLOSURE_NOTE_MAX in packages/projects/services/project-closure.service.ts. */
export const CLOSURE_NOTE_MAX = 1000;

export interface ClosureRequestCardProps {
  request: ClosureRequest;
  /** Hide the project link + title (when the card is already shown on that project's page). */
  hideProject?: boolean;
  /** Called after a successful approve / reject. */
  onResolved?: (action: "approve" | "reject") => void;
}

export function ClosureRequestCard({
  request,
  hideProject = false,
  onResolved,
}: ClosureRequestCardProps): JSX.Element {
  const resolve = useResolveClosureRequest();
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const noteId = useId();

  const projectName = request.project_title ?? request.project_id;
  const requester = request.requested_by_email ?? request.requested_by;

  const handleApprove = (): void => {
    resolve.mutate(
      { request, input: { action: "approve" } },
      {
        onSuccess: () => {
          toast.success("Closure approved — project closed");
          onResolved?.("approve");
        },
        onError: (err) =>
          toast.error(apiErrorMessage(err, "Failed to approve closure")),
      },
    );
  };

  const handleReject = (): void => {
    const trimmed = note.trim();
    if (!trimmed) {
      toast.error("A note is required to reject a closure request.");
      return;
    }
    resolve.mutate(
      { request, input: { action: "reject", note: trimmed } },
      {
        onSuccess: () => {
          toast.success("Closure request rejected");
          setRejecting(false);
          setNote("");
          onResolved?.("reject");
        },
        onError: (err) =>
          toast.error(apiErrorMessage(err, "Failed to reject closure")),
      },
    );
  };

  return (
    <Card className="border-amber-300/60 dark:border-amber-500/30">
      <CardContent className="space-y-3 p-5">
        {!hideProject && (
          <div className="min-w-0">
            <Link
              to={`/projects/${encodeURIComponent(request.project_id)}?tab=settings`}
              className="truncate font-semibold text-text-primary underline-offset-2 hover:underline"
            >
              {projectName}
            </Link>
            <div className="mt-0.5">
              <Badge variant="warning" className="gap-1">
                <Archive className="h-3 w-3" aria-hidden="true" />
                Closure requested
              </Badge>
            </div>
          </div>
        )}

        <div className="rounded-md border bg-subtle-bg/40 p-3 text-sm text-text-primary whitespace-pre-wrap">
          {request.reason}
        </div>

        <p className="text-xs text-text-secondary">
          Requested by {requester} • {timeAgo(request.created_at)}
        </p>

        {rejecting ? (
          <div className="space-y-2 pt-1">
            <Label htmlFor={noteId}>Rejection note *</Label>
            <Textarea
              id={noteId}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Explain what is needed before the project can close…"
              rows={3}
              maxLength={CLOSURE_NOTE_MAX}
              aria-required="true"
            />
            <div className="flex items-center gap-2">
              <Button
                variant="danger"
                size="sm"
                onClick={handleReject}
                disabled={resolve.isPending || !note.trim()}
              >
                {resolve.isPending && (
                  <Loader2
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                )}
                Confirm reject
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRejecting(false);
                  setNote("");
                }}
                disabled={resolve.isPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 pt-1">
            <Button
              size="sm"
              className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700"
              onClick={handleApprove}
              disabled={resolve.isPending}
              aria-label={`Approve closure of ${projectName}`}
            >
              {resolve.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Check className="h-4 w-4" aria-hidden="true" />
              )}
              Approve
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 border-red-300 text-red-700 hover:bg-red-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-500/10"
              onClick={() => setRejecting(true)}
              disabled={resolve.isPending}
              aria-label={`Reject closure of ${projectName}`}
            >
              <X className="h-4 w-4" aria-hidden="true" />
              Reject
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export interface PendingClosureRequestsSectionProps {
  /** Gate the fetch (pass the Dashboard's leadership/admin check). @default true */
  enabled?: boolean;
}

/** Dashboard section: the cross-project queue of pending closure requests. */
export function PendingClosureRequestsSection({
  enabled = true,
}: PendingClosureRequestsSectionProps): JSX.Element | null {
  const query = usePendingClosureRequests(enabled);
  const pending = query.data ?? [];

  if (!enabled) return null;

  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-semibold text-text-primary">
          Pending Closure Requests
          <Badge variant="warning" className="ml-2">
            {pending.length}
          </Badge>
        </h3>
        <p className="text-sm text-text-secondary">
          Project closures requested by PMs / SAs awaiting your approval
        </p>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <div
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
            aria-busy="true"
            aria-label="Loading closure requests"
          >
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-44 w-full rounded-lg" />
            ))}
          </div>
        ) : query.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Failed to load closure requests</AlertTitle>
            <AlertDescription className="flex flex-col items-start gap-3">
              <span>
                {apiErrorMessage(query.error, "An unexpected error occurred.")}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void query.refetch()}
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        ) : pending.length === 0 ? (
          <p className="py-8 text-center text-sm text-text-secondary">
            No pending closure requests.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {pending.map((req) => (
              <ClosureRequestCard key={req.id} request={req} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ClosureRequestCard;
